import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import AppHeader from '../components/AppHeader';
import { fetchAccountsUsage, fetchMyUsage } from '../api/dashboard';
import { AccountUsageRow, AccountsUsageResponse, MyUsageResponse } from '../types/dashboard';
import StatWidget from '../components/dashboard/StatWidget';
import UsageTable from '../components/dashboard/UsageTable';
import Skeleton from '../components/dashboard/Skeleton';
import EmptyState from '../components/dashboard/EmptyState';
import ErrorState from '../components/dashboard/ErrorState';
import { buildCsv, downloadCsv } from '../utils/csv';
import './UsageReportPage.css';

function formatIST(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function sumBy(rows: AccountUsageRow[], pick: (r: AccountUsageRow) => number): number {
  return rows.reduce((total, r) => total + pick(r), 0);
}

export default function UsageReportPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [meData, setMeData] = useState<MyUsageResponse | null>(null);
  const [accountsData, setAccountsData] = useState<AccountsUsageResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    const request = isAdmin ? fetchAccountsUsage().then(setAccountsData) : fetchMyUsage().then(setMeData);
    request.then(() => setStatus('ready')).catch(() => setStatus('error'));
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExportCsv = () => {
    if (!accountsData) return;
    const weeklyMessages = sumBy(accountsData.rows, (r) => r.last_7_days.turns);
    const weeklyMessagesTotal = weeklyMessages + accountsData.unattributed.turns;
    const pct = weeklyMessagesTotal > 0 ? Math.round((100 * weeklyMessages) / weeklyMessagesTotal) : 0;

    const rows = accountsData.rows.map((r) => [
      r.email ?? r.account_id,
      r.region ?? '—',
      r.status,
      r.last_7_days.chats_started,
      r.till_date.chats_started,
      r.last_7_days.turns,
      r.prev_7_days.turns,
      r.till_date.turns,
      r.last_7_days.active_days,
    ]);

    const csv = buildCsv(
      [
        [
          'Email', 'Region', 'Status',
          'Weekly Queries', 'Total Queries',
          'Weekly Messages', 'Prev Week Messages', 'Total Messages', 'Active Days (7d)',
        ],
        ...rows,
      ],
      [
        `Window: ${accountsData.window_start_day} to ${accountsData.window_end_day} (${accountsData.timezone})`,
        `Stats since: ${accountsData.stats_since_day}`,
        `Attributed share this week: ${pct}% (${weeklyMessages} of ${weeklyMessagesTotal})`,
        'Queries = distinct conversations. Messages = individual query/reply turns.',
        'Values count attributed activity only and are lower bounds.',
      ]
    );
    downloadCsv(`mira-usage-report-${accountsData.window_end_day}.csv`, csv);
  };

  // Same header shell as the chat app (AppHeader) — /dashboard is a full
  // page like ChatApp, not a bare content div, so it needs the same
  // .App > .app-main > <main> structure to get an identical fixed header.
  const renderShell = (children: React.ReactNode) => (
    <div className="App">
      <div className="app-main">
        <AppHeader backTo={{ to: '/', label: 'Back to All Messages' }} />
        <main>
          <div className="usage-report-page">{children}</div>
        </main>
      </div>
    </div>
  );

  if (status === 'error') {
    return renderShell(
      <ErrorState message="Failed to load the usage report." onRetry={load} />
    );
  }

  if (status === 'loading') {
    return renderShell(
      <>
        <div className="usage-report-header">
          <h1 className="usage-report-title">{isAdmin ? 'RM Usage Report' : 'My Usage'}</h1>
        </div>
        <Skeleton rows={6} />
      </>
    );
  }

  if (isAdmin && accountsData) {
    // All signed-in accounts, on equal footing regardless of provisioning path.
    const allRows = accountsData.rows;
    const activeAccounts = allRows.filter((r) => r.last_7_days.turns > 0).length;
    const totalActive = allRows.filter((r) => r.status === 'active').length;

    // Messages = individual query/reply turns (what "Weekly/Total Queries"
    // used to mean here before Queries was redefined as distinct
    // conversations, matching the self view).
    const weeklyMessages = sumBy(accountsData.rows, (r) => r.last_7_days.turns);
    const weeklyMessagesTotal = weeklyMessages + accountsData.unattributed.turns;
    const attributedPct = weeklyMessagesTotal > 0 ? Math.round((100 * weeklyMessages) / weeklyMessagesTotal) : 0;
    const totalMessages = sumBy(accountsData.rows, (r) => r.till_date.turns);

    // Queries = distinct conversations (chats_started), not turns.
    const weeklyQueries = sumBy(accountsData.rows, (r) => r.last_7_days.chats_started);
    const totalQueries = sumBy(accountsData.rows, (r) => r.till_date.chats_started);

    return renderShell(
      <>
        <div className="usage-report-header">
          <div className="usage-report-title-row">
            <h1 className="usage-report-title">Usage Report</h1>
            <button className="usage-report-export-button" onClick={handleExportCsv}>⤓ Export CSV</button>
          </div>
          <p className="usage-report-subtitle">
            Signed-in activity on MIRA One · as of {formatIST(accountsData.generated_at)} IST
          </p>
          <p className="usage-report-attributed-line">
            {attributedPct}% of this week's messages attributed ({weeklyMessages} of {weeklyMessagesTotal})
          </p>
        </div>

        <div className="usage-report-widgets-grid">
          <StatWidget
            title="Active Accounts"
            columns={[{ label: `Out of ${totalActive} accounts`, value: String(activeAccounts) }]}
          />
          <StatWidget
            title="Queries"
            columns={[
              { label: 'This Week', value: String(weeklyQueries) },
              { label: 'Till Date', value: String(totalQueries) },
            ]}
          />
          <StatWidget
            title="Messages"
            columns={[
              { label: 'This Week', value: String(weeklyMessages) },
              { label: 'Till Date', value: String(totalMessages) },
            ]}
          />
        </div>

        {allRows.length === 0 ? (
          <EmptyState message="No internal users have signed in yet" />
        ) : (
          <>
            <UsageTable rows={allRows} />
            <p className="usage-report-footer">{allRows.length} accounts shown</p>
          </>
        )}
      </>
    );
  }

  if (!isAdmin && meData) {
    const { me, extras } = meData;

    return renderShell(
      <>
        <div className="usage-report-header">
          <div className="usage-report-title-row">
            <h1 className="usage-report-title">My Usage</h1>
          </div>
          <p className="usage-report-subtitle">
            Signed-in activity on MIRA One · as of {formatIST(meData.generated_at)} IST
          </p>
        </div>

        <div className="usage-report-widgets-grid">
          <StatWidget
            title="Active Days"
            columns={[{ label: 'This Week', value: `${me.last_7_days.active_days}/7` }]}
          />
          <StatWidget
            title="Queries"
            columns={[
              { label: 'This Week', value: String(extras.queries.last_7_days) },
              { label: 'Till Date', value: String(extras.queries.till_date) },
            ]}
          />
          <StatWidget
            title="Messages"
            columns={[
              { label: 'This Week', value: String(extras.messages.last_7_days) },
              { label: 'Till Date', value: String(extras.messages.till_date) },
            ]}
          />
          {/* Shares has no weekly figure — shares themselves only live 30 days
              server-side (TTL), so a 7-day slice of that isn't a meaningful
              pair alongside a "lifetime" number the way the other widgets have. */}
          <StatWidget
            title="Shares"
            columns={[{ label: 'Last 30 Days', value: String(extras.shares.last_30_days) }]}
          />
        </div>

        <p className="usage-report-signed-out-note">
          Queries asked while signed out are not counted here or anywhere.
        </p>
      </>
    );
  }

  return null;
}
